import { errorAndWarningAttributes, StateToTypeMap, UpdateField } from './FormReducer'

export type PickByType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]

export interface ErrorCodes {
  requiredField: string
  numericField: string
  alphanumericField: string
  nonEmptyField: string
}

export interface BaseErrorType {
  error: ErrorType
  errorListeners?: (keyof ErrorCodes)[]
  warning: ErrorType
  warningListeners?: (keyof ErrorCodes)[]
}

export type FormFieldType = 'text' | 'multiText' | 'list' | 'select' | 'multiSelect'

export type FormFieldTypeToInterface<T extends FormFieldType> = T extends 'text'
  ? FormReducerSingleTextField
  : T extends 'multiText'
    ? FormReducerMultiTextField
    : T extends 'list'
      ? FormReducerListField
      : T extends 'select'
        ? FormReducerSelectField
        : T extends 'multiSelect'
          ? FormReducerMultiSelectField
          : never

export function createFormField<T extends FormFieldType>(
  type: T,
  value: T extends 'text' | 'select' ? string : string[],
): FormFieldTypeToInterface<T> {
  return {
    value,
    type,
    ...errorAndWarningAttributes,
  } as FormFieldTypeToInterface<T>
}

// Define the keys of the fields that match the correct element types
export interface FormReducerSingleTextField extends BaseErrorType {
  value: string
  type: 'text'
}
export interface FormReducerMultiTextField extends BaseErrorType {
  value: string[]
  type: 'multiText'
}
export interface FormReducerListField extends BaseErrorType {
  value: string[]
  type: 'list'
}

export interface FormReducerSelectField extends BaseErrorType {
  value: string
  type: 'select'
}
export interface FormReducerMultiSelectField extends BaseErrorType {
  value: string[]
  type: 'multiSelect'
}

export type ErrorType = string | undefined

export interface ValidationStatus<T extends keyof StateToTypeMap> {
  id: keyof StateToTypeMap[T]
  passesValidation: boolean
}

export type ValidationObject<T extends keyof StateToTypeMap> = {
  [K in keyof ErrorCodes]?: ValidationStatus<T>[]
}

// Get the keys of the fields that have a value and an error field
export type BaseValidationField<T> = {
  [K in keyof T]: T[K] extends { value: any; error: ErrorType } ? K : never
}[keyof T]

// Create a new validation function that will add the error field to the validation object
export function withValidationFactory<T extends keyof StateToTypeMap>(validationObject: ValidationObject<T>) {
  // wrap the updateField with the desired error fields, e.g. client_name: withErrors(emptyInputField, ['requiredField'])
  return function withValidation<U>(
    updateField: UpdateField,
    id: keyof StateToTypeMap[T],
    errorFields: (keyof ErrorCodes)[],
    warningFields: (keyof ErrorCodes)[],
    passesValidationOnInit: boolean = false,
  ): U {
    errorFields.forEach(errorField => {
      // register the error field in the validation object
      validationObject[errorField] = [
        ...(validationObject[errorField] || ([] as ValidationStatus<T>[])),
        { id, passesValidation: passesValidationOnInit },
      ] as ValidationStatus<T>[]
    })
    // return the updated field with the error listeners and the validation object
    return {
      ...updateField,
      errorListeners: errorFields,
      warningListeners: warningFields,
      validationObject: validationObject,
    } as U
  }
}

export const addListItemString = (currentValues: string[]) => {
  return [...currentValues, '']
}

export const removeListItem = (currentValues: string[], idx: number) => {
  const newTextList = Array.from(currentValues)
  newTextList.splice(idx, 1)
  return newTextList
}

export const replaceListItem = (currentValues: string[], value: string, idx: number) => {
  const newTextList = Array.from(currentValues)
  newTextList[idx] = value
  return newTextList
}

export const addSelectValueString = (currentValues: string[], value: string) => {
  if (value === '' || currentValues.find(currentValue => currentValue === value)) return currentValues
  return [...currentValues, value]
}

export const removeSelectValueString = (currentValues: string[], value: string) => {
  if (!currentValues.find(currentValue => currentValue === value)) return currentValues
  const returnArray = Array.from(currentValues)
  returnArray.splice(currentValues.indexOf(value), 1)
  return returnArray
}

export const addInputItemToArray = (currentValues: string[], value: string) => {
  if (currentValues.includes(value)) return currentValues
  return [...currentValues, value]
}
